import React, { useRef, useEffect, useState, useCallback } from "react";
import { debounce, divide } from "lodash";
import { floor, multiply } from "mathjs";
import textfit from "textfit";

import { ChartClassByType } from "../../utils/option_main";
import { usePrevious } from "../../utils/hooks";

import { EDIT_PAGE_FORM_WIDTH } from "../../constants/form";

import Chart from "../Chart";
import ChartError from "../ChartError";

import { IWrokspaceData, ITableData } from "../../types/table-type";
import { IChartFormValue } from "../../types/form-type";
import { ChartErrorType, ChartType, PageType } from "../../types/global-type";
import { IEChartsOption } from "../../types/echarts-type";

import { Container, ValueMessage } from "./styled";

interface IProps {
  workspaceData: IWrokspaceData;
  tableData: ITableData;
  formValue: IChartFormValue;
  from: PageType;
  onload?: () => void;
  isExpand: boolean;
}

const ChartView: React.FC<IProps> = ({ workspaceData, tableData, formValue, from, onload, isExpand }) => {
  const { chartType } = formValue;

  const chartViewRef = useRef<HTMLDivElement>(null);
  const numberContainerRef = useRef<HTMLDivElement>(null);

  const [option, setOption] = useState<Object | null>(null);
  const [error, setError] = useState<ChartErrorType | null>(null);
  const [windowResizeChange, setWindowResizeChange] = useState<boolean>(true);
  const [pieValue, setPieValue] = useState<string>("");
  // 圆内接四边形 Cyclic quadrilateral 以下简称 CQ
  const [CQLeft, setCQLeft] = useState<number>(0);
  const [CQTop, setCQTop] = useState<number>(0);
  const [sideLength, setSideLength] = useState<number>(0);

  // 第一次计算 option 完成后发送
  const prevOption = usePrevious(option);
  useEffect(() => {
    if (!prevOption && !!option) {
      if (onload) onload();
    }
  }, [option, prevOption, onload]);

  const calcCQGeometricProperties = useCallback(
    (value?: { center: [number, number]; radius: number }) => {
      if (!value) return;
      if (chartType !== ChartType.PIE_RING) return;
      const { center, radius } = value;
      setCQLeft(center[0] - floor(divide(radius, Math.sqrt(2))));
      setCQTop(center[1] - floor(divide(radius, Math.sqrt(2))));
      setSideLength(floor(multiply(radius, Math.sqrt(2))));
    },
    [chartType]
  );

  const handleWindowResize = useCallback(
    debounce(() => {
      setWindowResizeChange(true);
    }, 300),
    []
  );

  const handleValueMessageResize = useCallback(() => {
    if (!sideLength) return null;
    numberContainerRef?.current &&
      textfit(numberContainerRef?.current, {
        minFontSize: 10,
        maxFontSize: 500,
      });
  }, [sideLength]);

  useEffect(() => {
    if (pieValue !== undefined && chartType === ChartType.PIE_RING) {
      handleWindowResize();
      handleValueMessageResize();
    }
  }, [pieValue, handleWindowResize, handleValueMessageResize, chartType]);

  useEffect(() => {
    window.addEventListener("resize", handleWindowResize, false);
    window.addEventListener("resize", handleValueMessageResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize, false);
      window.removeEventListener("resize", handleValueMessageResize);
    };
  }, [handleWindowResize, handleValueMessageResize]);

  useEffect(() => {
    if (!workspaceData || !tableData || !formValue || !chartType || !from) return;
    if (windowResizeChange) {
      setWindowResizeChange(false);
      return;
    }
    const ChartClass = ChartClassByType[chartType];
    const chart = new ChartClass({ workspaceData, tableData, formValue, chartType });
    chart.setCanvasWidth(document.documentElement.clientWidth - (from === PageType.EDIT ? EDIT_PAGE_FORM_WIDTH : 0));
    chart.setCanvasHeight(document.documentElement.clientHeight);
    const option = chart.getOption();
    calcCQGeometricProperties(chart.innerCircleGeometricProperties);
    if (chartType === ChartType.PIE_RING) {
      setPieValue(chart.centerValue);
    }
    const err = chart.getChartError();
    if (err) {
      setError(err);
      setOption({});
    } else {
      setError(null);
      setOption(option);
    }
  }, [workspaceData, tableData, formValue, chartType, from, windowResizeChange, calcCQGeometricProperties]);

  if (error) {
    return <ChartError errorType={error} chartType={chartType} isExpand={isExpand} />;
  }

  if (!option) {
    return null;
  }

  if (!pieValue && chartType === ChartType.PIE_RING) return null;

  return (
    <Container ref={chartViewRef}>
      {chartType === ChartType.PIE_RING && (
        <ValueMessage ref={numberContainerRef} left={CQLeft} top={CQTop} sideLength={sideLength}>
          {pieValue}
        </ValueMessage>
      )}
      <Chart option={option as IEChartsOption} />
    </Container>
  );
};

export default ChartView;
